import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { Clock, Calendar } from "lucide-react";
import type { BlogPost, BlogCategory } from "@/data/blog";
import { getPostBySlug, getRelatedPosts, getCategoryBySlug, type BlogBlock } from "@/data/blog";
import { BlogShell } from "@/components/blog/BlogLayout";
import { BlogCard } from "@/components/blog/BlogCard";
import { ArticleCTA, NewsletterBox } from "@/components/blog/ArticleCTA";

export const Route = createFileRoute("/blog/$slug")({
  loader: ({ params }) => {
    const post = getPostBySlug(params.slug);
    if (!post) throw notFound();
    return {
      post,
      category: getCategoryBySlug(post.category),
      related: getRelatedPosts(params.slug, 3),
    };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return {};
    const { post } = loaderData;
    const url = `https://www.flash-web.ch/blog/${params.slug}`;
    return {
      meta: [
        { title: post.metaTitle },
        { name: "description", content: post.metaDescription },
        { name: "author", content: post.author.name },
        { name: "keywords", content: post.tags.join(", ") },
        { property: "og:title", content: post.metaTitle },
        { property: "og:description", content: post.metaDescription },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { property: "article:published_time", content: post.publishedAt },
        { property: "article:author", content: post.author.name },
        { property: "article:section", content: loaderData.category?.name ?? "" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: post.metaTitle },
        { name: "twitter:description", content: post.metaDescription },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: post.title,
            description: post.metaDescription,
            image: post.coverImage?.startsWith("http") ? post.coverImage : `https://www.flash-web.ch${post.coverImage ?? ""}`,
            datePublished: post.publishedAt,
            dateModified: post.updatedAt ?? post.publishedAt,
            author: { "@type": "Organization", name: "FlashWeb", url: "https://www.flash-web.ch" },
            publisher: {
              "@type": "Organization",
              name: "FlashWeb Agency",
              url: "https://www.flash-web.ch",
              logo: { "@type": "ImageObject", url: "https://www.flash-web.ch/favicon.ico" },
            },
            mainEntityOfPage: { "@type": "WebPage", "@id": url },
            articleSection: loaderData.category?.name,
            keywords: post.tags.join(", "),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://www.flash-web.ch/" },
              { "@type": "ListItem", position: 2, name: "Blog", item: "https://www.flash-web.ch/blog" },
              { "@type": "ListItem", position: 3, name: post.title, item: url },
            ],
          }),
        },
        post.faqs.length > 0 && {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: post.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
      ].filter(Boolean) as Array<{ type: string; children: string }>,
    };
  },
  notFoundComponent: () => (
    <BlogShell>
      <div className="mx-auto max-w-3xl px-6 py-24 text-center">
        <h1 className="text-3xl font-bold">Articolo non trovato</h1>
        <Link to="/blog" className="mt-6 inline-block text-primary hover:underline">Torna al blog</Link>
      </div>
    </BlogShell>
  ),
  errorComponent: ({ error, reset }) => (
    <BlogShell>
      <div className="mx-auto max-w-3xl px-6 py-24 text-center">
        <h1 className="text-2xl font-bold">Errore: {error.message}</h1>
        <button onClick={reset} className="mt-4 text-primary hover:underline">Riprova</button>
      </div>
    </BlogShell>
  ),
  component: ArticlePage,
});

function Block({ block }: { block: BlogBlock }) {
  switch (block.type) {
    case "p":
      return <p className="mt-5 text-base leading-relaxed text-muted-foreground sm:text-[17px]">{block.text}</p>;
    case "h2":
      return <h2 id={block.id} className="mt-14 scroll-mt-24 font-display text-3xl font-bold tracking-tight text-foreground sm:text-4xl">{block.text}</h2>;
    case "h3":
      return <h3 id={block.id} className="mt-8 scroll-mt-24 text-xl font-semibold text-foreground">{block.text}</h3>;
    case "ul":
      return <ul className="mt-5 space-y-2 pl-5 text-muted-foreground marker:text-primary [&>li]:list-disc">{block.items.map((it, i) => <li key={i}>{it}</li>)}</ul>;
    case "ol":
      return <ol className="mt-5 space-y-2 pl-5 text-muted-foreground marker:font-bold marker:text-primary [&>li]:list-decimal">{block.items.map((it, i) => <li key={i}>{it}</li>)}</ol>;
    case "quote":
      return <blockquote className="mt-6 border-l-4 border-primary/60 bg-card/40 p-5 italic text-foreground">"{block.text}"{block.cite && <cite className="mt-2 block text-sm not-italic text-muted-foreground">— {block.cite}</cite>}</blockquote>;
    case "callout":
      return (
        <aside className="mt-8 overflow-hidden rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 via-card/60 to-card p-6">
          <div className="text-xs font-semibold uppercase tracking-wider text-primary">{block.title}</div>
          <p className="mt-2 text-base text-foreground">{block.text}</p>
        </aside>
      );
    case "checklist":
      return (
        <ul className="mt-6 space-y-3 rounded-2xl border border-border/60 bg-card/40 p-6">
          {block.items.map((it, i) => (
            <li key={i} className="flex items-start gap-3 text-sm text-foreground">
              <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-primary/20 text-[11px] font-bold text-primary">✓</span>
              <span>{it}</span>
            </li>
          ))}
        </ul>
      );
    case "cta":
      return <ArticleCTA />;
  }
}

function ArticlePage() {
  const data = Route.useLoaderData() as { post: BlogPost; category: BlogCategory | undefined; related: BlogPost[] };
  const { post, category, related } = data;
  const toc = post.blocks.filter((b): b is { type: "h2"; text: string; id: string } => b.type === "h2");
  const formattedDate = new Date(post.publishedAt).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" });

  return (
    <BlogShell>
      {/* Cover */}
      <header className="relative overflow-hidden border-b border-border/40" style={{ background: post.coverGradient }}>
        <img
          src={post.coverImage}
          alt={post.title}
          className="absolute inset-0 h-full w-full object-cover opacity-60"
          loading="eager"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-background/30" />
        <div className="relative mx-auto max-w-4xl px-6 py-20 sm:py-28">
          <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground">
            <ol className="flex flex-wrap items-center gap-2">
              <li><Link to="/" className="hover:text-foreground">Home</Link></li>
              <li aria-hidden>/</li>
              <li><Link to="/blog" className="hover:text-foreground">Blog</Link></li>
              {category && <>
                <li aria-hidden>/</li>
                <li><Link to="/blog/categoria/$slug" params={{ slug: category.slug }} className="hover:text-foreground">{category.name}</Link></li>
              </>}
            </ol>
          </nav>
          {category && (
            <span className="mt-6 inline-block rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-primary backdrop-blur">
              {category.name}
            </span>
          )}
          <h1 className="mt-4 font-display text-4xl font-bold leading-[1.1] tracking-tight text-foreground sm:text-5xl lg:text-6xl">{post.title}</h1>
          <p className="mt-5 max-w-2xl text-lg text-muted-foreground">{post.excerpt}</p>
          <div className="mt-7 flex flex-wrap items-center gap-5 text-sm text-muted-foreground">
            <span className="inline-flex items-center gap-2">
              <span className="grid h-8 w-8 place-items-center rounded-full bg-primary/20 text-[11px] font-bold text-primary">
                {post.author.name.split(" ").map((w) => w[0]).join("").slice(0, 2)}
              </span>
              <span className="text-foreground font-medium">{post.author.name}</span>
              <span className="text-muted-foreground">· {post.author.role}</span>
            </span>
            <span className="inline-flex items-center gap-1.5"><Calendar className="h-4 w-4" /> {formattedDate}</span>
            <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4" /> {post.readingMinutes} min di lettura</span>
          </div>
        </div>
      </header>

      {/* Body */}
      <div className="mx-auto grid max-w-6xl gap-12 px-6 py-12 lg:grid-cols-[1fr_280px] lg:py-16">
        <article>
          {toc.length > 1 && (
            <nav aria-label="Sommario" className="mb-10 rounded-2xl border border-border/60 bg-card/40 p-5">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-primary">Sommario</h2>
              <ol className="mt-3 space-y-2 text-sm">
                {toc.map((t, i) => (
                  <li key={t.id}>
                    <a href={`#${t.id}`} className="text-muted-foreground hover:text-foreground">
                      <span className="mr-2 font-mono text-xs text-primary">{String(i + 1).padStart(2, "0")}</span>
                      {t.text}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>
          )}

          {post.blocks.map((b, i) => <Block key={i} block={b} />)}

          {/* FAQ */}
          {post.faqs.length > 0 && (
            <section className="mt-16">
              <h2 className="text-2xl font-bold sm:text-3xl">Domande frequenti</h2>
              <div className="mt-6 divide-y divide-border/60 rounded-2xl border border-border/60 bg-card/40">
                {post.faqs.map((f, i) => (
                  <details key={i} className="group p-5 [&_summary::-webkit-details-marker]:hidden">
                    <summary className="flex cursor-pointer items-center justify-between gap-4 font-semibold text-foreground">
                      {f.q}
                      <span className="text-primary transition-transform group-open:rotate-45">+</span>
                    </summary>
                    <p className="mt-3 text-sm text-muted-foreground">{f.a}</p>
                  </details>
                ))}
              </div>
            </section>
          )}

          {/* Final CTA */}
          <div className="mt-12">
            <ArticleCTA />
          </div>

          {/* Tags */}
          <div className="mt-10 flex flex-wrap gap-2">
            {post.tags.map((t) => (
              <span key={t} className="rounded-full border border-border bg-card/40 px-3 py-1 text-xs text-muted-foreground">#{t}</span>
            ))}
          </div>
        </article>

        {/* Sidebar */}
        <aside className="space-y-6 lg:sticky lg:top-24 lg:self-start">
          <div className="rounded-2xl border border-border/60 bg-card/40 p-5">
            <h3 className="text-sm font-semibold">L'autore</h3>
            <p className="mt-2 text-sm text-foreground">{post.author.name}</p>
            <p className="text-xs text-muted-foreground">{post.author.role}</p>
            <p className="mt-3 text-xs text-muted-foreground">Scrive di siti web, SEO e business online per imprenditori in Ticino e Italia.</p>
          </div>
          <NewsletterBox />
        </aside>
      </div>

      {/* Related */}
      {related.length > 0 && (
        <section className="mx-auto max-w-7xl px-6 pb-20">
          <h2 className="text-2xl font-bold sm:text-3xl">Articoli correlati</h2>
          <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((p) => <BlogCard key={p.slug} post={p} />)}
          </div>
        </section>
      )}
    </BlogShell>
  );
}
